import React from 'react';
import PropTypes from 'prop-types';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useProfile } from '../../contexts/ProfileContext';
import '../../assets/styles/sidebar.css';

// Component to render individual nav items - moved outside to fix scope issue
const NavItem = ({ item, collapsed, hasRequiredRole, isAnyChildActive }) => {
  // Check if the user has the required role for this item
  if (!hasRequiredRole(item.allowedRoles)) {
    return null;
  }

  // Filter children based on roles
  const visibleChildren = item.children?.filter(child => hasRequiredRole(child.allowedRoles));

  // If no children are visible, hide the parent
  if (item.children && (!visibleChildren || visibleChildren.length === 0)) {
    return null;
  }

  // Check if this parent has any active children
  const hasActiveChild = isAnyChildActive(visibleChildren);

  const content =
    visibleChildren && !collapsed ? (
      <li key={item.id} className="nav-item">
        <div className={`nav-link nav-parent ${hasActiveChild ? 'has-active-child' : ''}`}>
          <div className="nav-link-content">
            <i className={`nav-icon ${item.icon}`}></i>
            <span className="nav-text">{item.label}</span>
          </div>
        </div>
        <ul className="nav-children">
          {visibleChildren.map((child, index) => (
            <li key={index} className="nav-child-item">
              <NavLink
                to={child.path}
                end={child.exact !== false}
                className={({ isActive }) => `nav-child-link ${isActive ? 'active' : ''}`}
              >
                <span className="nav-child-text">{child.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </li>
    ) : (
      <li key={item.id} className="nav-item">
        <NavLink
          to={item.path}
          end={!item.children || item.children.length === 0}
          className={({ isActive }) => `nav-link ${isActive || hasActiveChild ? 'active' : ''}`}
        >
          <div className="nav-link-content">
            <i className={`nav-icon ${item.icon}`}></i>
            {!collapsed && <span className="nav-text">{item.label}</span>}
          </div>
        </NavLink>
      </li>
    );

  return content;
};

// PropTypes for NavItem component
NavItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    allowedRoles: PropTypes.arrayOf(PropTypes.string),
    children: PropTypes.arrayOf(
      PropTypes.shape({
        path: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        exact: PropTypes.bool,
      })
    ),
  }).isRequired,
  collapsed: PropTypes.bool.isRequired,
  hasRequiredRole: PropTypes.func.isRequired,
  isAnyChildActive: PropTypes.func.isRequired,
};

function Sidebar({ collapsed, mobileVisible, isMobile }) {
  const { t } = useTranslation();
  const { isSystemAdmin, isCustomerAdmin, isSupportUser } = useProfile();
  const location = useLocation();

  const getNavigationItems = () => {
    // Array order IS the rendered order — hasRequiredRole only filters, it never
    // sorts. So a new item appended here lands at the BOTTOM of the nav for every
    // role that can see it: that is how the CustomerUser landing page ended up
    // below Resources (PR #23) and Installer Check (PR #24). Landing pages first.
    return [
      {
        id: 'home',
        path: '/',
        label: t('nav.home'),
        icon: 'bi-house-fill',
        allowedRoles: ['CustomerUser'],
      },
      {
        // Same path as 'home' above, but the two role sets are disjoint, so a
        // given user only ever sees one of them.
        id: 'dashboard',
        path: '/',
        label: t('nav.dashboard'),
        icon: 'bi-speedometer2',
        allowedRoles: ['CustomerAdministrator'],
      },
      {
        id: 'customers',
        path: '/customers',
        label: t('nav.customers'),
        icon: 'bi-building-fill',
        allowedRoles: ['SystemAdministrator'],
      },
      {
        id: 'users',
        path: '/organization/users',
        label: t('nav.users'),
        icon: 'bi-person-fill',
        allowedRoles: ['CustomerAdministrator'],
      },
      {
        id: 'administration',
        path: '/administration',
        label: t('nav.administration'),
        icon: 'bi-person-fill-gear',
        allowedRoles: ['SystemAdministrator'],
      },
      {
        id: 'support-users',
        path: '/support-users',
        label: t('nav.supportUsers'),
        icon: 'bi-headset',
        allowedRoles: ['SystemAdministrator'],
      },
      {
        id: 'monitoring',
        path: '/monitoring',
        label: t('nav.monitoring'),
        icon: 'bi-activity',
        allowedRoles: ['SystemAdministrator'],
        children: [
          { path: '/monitoring', label: t('nav.monitoringDashboard'), exact: true },
          { path: '/monitoring/errors', label: t('nav.monitoringErrors') },
          { path: '/monitoring/schedules', label: t('nav.monitoringSchedules') },
        ],
      },
      {
        id: 'telemetry',
        path: '/telemetry/activity',
        label: t('nav.telemetry'),
        icon: 'bi-broadcast',
        allowedRoles: ['SystemAdministrator', 'SupportUser'],
        children: [
          { path: '/telemetry/activity', label: t('nav.telemetryActivity') },
          { path: '/telemetry/health', label: t('nav.telemetryHealth') },
        ],
      },
      {
        id: 'installers',
        path: '/installers',
        label: t('nav.installers'),
        icon: 'bi-download',
        allowedRoles: [
          'SystemAdministrator',
          'CustomerAdministrator',
          'SupportUser',
          'CustomerUser',
        ],
      },
      {
        id: 'installer-check',
        path: '/installer-check',
        label: t('nav.installerCheck'),
        icon: 'bi-patch-check',
        allowedRoles: [
          'SystemAdministrator',
          'CustomerAdministrator',
          'SupportUser',
          'CustomerUser',
        ],
      },
      {
        id: 'support-customers',
        path: '/customers',
        label: t('nav.customers'),
        icon: 'bi-building-fill',
        allowedRoles: ['SupportUser'],
      },
    ];
  };

  const navigationItems = getNavigationItems();

  // Helper function to check if user has required role
  const hasRequiredRole = allowedRoles => {
    if (!allowedRoles || allowedRoles.length === 0) return true;
    const currentRole = isSystemAdmin
      ? 'SystemAdministrator'
      : isCustomerAdmin
        ? 'CustomerAdministrator'
        : isSupportUser
          ? 'SupportUser'
          : 'CustomerUser';
    return allowedRoles.includes(currentRole);
  };

  // Helper function to check if any child is active
  const isAnyChildActive = children => {
    if (!children) return false;
    return children.some(child => {
      if (child.exact) {
        return location.pathname === child.path;
      }
      return location.pathname.startsWith(child.path);
    });
  };

  return (
    <div
      className={`sidebar ${collapsed ? 'collapsed' : ''} ${isMobile && mobileVisible ? 'show' : ''}`}
    >
      <div className="sidebar-content">
        <nav className="sidebar-nav">
          <ul className="nav-list">
            {navigationItems.map(item => (
              <NavItem
                key={item.id}
                item={item}
                collapsed={collapsed}
                hasRequiredRole={hasRequiredRole}
                isAnyChildActive={isAnyChildActive}
              />
            ))}
          </ul>
        </nav>
      </div>
    </div>
  );
}

// PropTypes for main Sidebar component
Sidebar.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  mobileVisible: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
};

export default Sidebar;
